import React, { useEffect, useState } from 'react';
import { LayerConfig } from '../data/layersConfig';
import FeaturePopup from './FeaturePopup';
import { createPortal } from 'react-dom';
import { Source, Layer, useMap as useReactMap } from 'react-map-gl/mapbox';
import { useMap as useMapContext } from '../context/MapContext';
import mapboxgl from 'mapbox-gl';
import type { MapLayerMouseEvent } from 'mapbox-gl';



import { permanentLayersGeojson } from '../data/permanentLayersGeojson';
import LayerActionPanel from './LayerActionPanel';
import sessionManager from '../utils/sessionManager';

interface EnhancedPermanentLayersProps {
  layers: LayerConfig[];
  visibleLayers: string[];
  measureActive?: boolean;
  layerActionPanelOpen?: boolean;
  onLayerActionClose?: () => void;
}

interface PopupState {
  feature: {
    name: string;
    properties: Record<string, any>;
    layerType: string;
  };
  position: { x: number; y: number };
}

const EnhancedPermanentLayers: React.FC<EnhancedPermanentLayersProps> = ({
  layers,
  visibleLayers,
  measureActive = false,
  layerActionPanelOpen = false,
  onLayerActionClose = () => { }
}) => {
  // Get map instance from react-map-gl context and labels visibility from app context
  const { current: map } = useReactMap();
  const { labelsVisible } = useMapContext();

  const [popupState, setPopupState] = useState<PopupState | null>(null);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const user = await sessionManager.getCurrentUser();
      if (user && user.canEdit) {
        setCanEdit(true);
      }
    };
    checkPermission();
  }, []);

  // Helper function to add highlight to a feature
  const addFeatureHighlight = (layerId: string, featureName: string) => {
    if (!map) return;
    try {
      const m = map && typeof map.getMap === 'function' ? map.getMap() : map;
      const mbx = m as mapboxgl.Map;
      const geojson = geojsons[layerId];
      if (!geojson) return;

      // Find the feature by name or code (for PPEM features)
      let match;
      if (layerId === 'petite-mine') {
        // For PPEM features, match by Code (extract from header format "2649 D2")
        const codeFromHeader = featureName.replace(' D2', '');
        match = geojson.features.find(f => f.properties && f.properties.Code === codeFromHeader);
      } else {
        // For other features, match by name
        match = geojson.features.find(f => f.properties && (f.properties.Nom === featureName || f.properties.Name === featureName));
      }

      if (!match) return;

      // Remove existing highlight if present
      const highlightId = 'popup-feature-highlight';
      if (mbx.getLayer(highlightId)) mbx.removeLayer(highlightId);
      if (mbx.getSource(highlightId)) mbx.removeSource(highlightId);

      // Add new highlight
      mbx.addSource(highlightId, { type: 'geojson', data: match });

      // Thin highlight styling based on layer type
      if (layerId === 'petite-mine') {
        mbx.addLayer({
          id: highlightId,
          type: 'line',
          source: highlightId,
          paint: {
            'line-color': '#ff4444',
            'line-width': 1.5,
            'line-opacity': 0.9
          }
        });
      } else {
        mbx.addLayer({
          id: highlightId,
          type: 'line',
          source: highlightId,
          paint: {
            'line-color': '#1976d2',
            'line-width': 1.5,
            'line-opacity': 0.9
          }
        });
      }
    } catch (e) {
      console.warn('Failed to add feature highlight:', e);
    }
  };

  // Helper function to remove feature highlight
  const removeFeatureHighlight = () => {
    if (!map) return;
    try {
      const m = map && typeof map.getMap === 'function' ? map.getMap() : map;
      const mbx = m as mapboxgl.Map;
      const highlightId = 'popup-feature-highlight';
      if (mbx.getLayer(highlightId)) mbx.removeLayer(highlightId);
      if (mbx.getSource(highlightId)) mbx.removeSource(highlightId);
    } catch (e) {
      console.warn('Failed to remove feature highlight:', e);
    }
  };

  // Mapbox GL rendering logic
  // Only render visible permanent layers
  const [geojsons, setGeojsons] = useState<Record<string, GeoJSON.FeatureCollection>>({});
  const [loading, setLoading] = useState(false);

  // Track all layer IDs for interactive layers
  const [interactiveLayers, setInteractiveLayers] = useState<string[]>([]);
  // Always up-to-date ref for use in native event handler
  const interactiveLayersRef = React.useRef<string[]>([]);
  useEffect(() => { interactiveLayersRef.current = interactiveLayers; }, [interactiveLayers]);

  useEffect(() => {
    // Load GeoJSON for visible layers only
    const fetchData = async () => {
      console.log('EnhancedPermanentLayers: Fetching data for visible layers:', visibleLayers);
      setLoading(true);
      const newGeojsons: Record<string, GeoJSON.FeatureCollection> = {};
      const newInteractiveLayers: string[] = [];

      for (const layerConfig of layers) {
        // Only render layers that are in the visibleLayers array
        if (!visibleLayers.includes(layerConfig.id)) continue;

        console.log(`Processing visible layer: ${layerConfig.name} (${layerConfig.id})`);

        // Find the corresponding geojson config to get the URL
        const geojsonConfig = permanentLayersGeojson.find(l => l.id === layerConfig.id);
        if (!geojsonConfig) {
          console.error(`No GeoJSON config found for layer ${layerConfig.id}`);
          continue;
        }

        // Handle group layers (like zones-commerciales)
        if (geojsonConfig.type === 'group' && geojsonConfig.children) {
          for (const childConfig of geojsonConfig.children) {
            // Only process child layers that are visible
            if (!visibleLayers.includes(childConfig.id)) continue;

            if (!childConfig.url) {
              console.error(`No URL found for child layer ${childConfig.id}`);
              continue;
            }
            try {
              const resp = await fetch(childConfig.url, { credentials: 'include' });
              if (!resp.ok) {
                console.error(`Failed to fetch GeoJSON for ${childConfig.id}: Status ${resp.status}`);
                continue;
              }
              const data = await resp.json();
              newGeojsons[childConfig.id] = data;

              // Add appropriate layer ID to interactive layers for popup clicks
              const hasFeatures = data.features && data.features.length > 0;
              const geomType = hasFeatures ? data.features[0]?.geometry?.type : null;
              const isPointLayer = geomType === 'Point';
              newInteractiveLayers.push(isPointLayer ? `${childConfig.id}-circle` : `${childConfig.id}-fill`);
              console.log(`Successfully loaded and staged child layer ${childConfig.id}`);
            } catch (e) {
              console.error(`Error parsing or fetching GeoJSON for child layer ${childConfig.id}:`, e);
            }
          }
        } else {
          // Handle regular layers
          if (!geojsonConfig.url) {
            console.error(`No URL found for layer ${layerConfig.id}`);
            continue;
          }
          try {
            const resp = await fetch(geojsonConfig.url, { credentials: 'include' });
            if (!resp.ok) {
              console.error(`Failed to fetch GeoJSON for ${layerConfig.id}: Status ${resp.status}`);
              continue; // Skip to next layer
            }
            const data = await resp.json();
            newGeojsons[layerConfig.id] = data;

            // Add appropriate layer ID to interactive layers for popup clicks
            const hasFeatures = data.features && data.features.length > 0;
            const geomType = hasFeatures ? data.features[0]?.geometry?.type : null;
            const isPointLayer = geomType === 'Point';
            newInteractiveLayers.push(isPointLayer ? `${layerConfig.id}-circle` : `${layerConfig.id}-fill`);
            console.log(`Successfully loaded and staged ${layerConfig.id}`);
          } catch (e) {
            console.error(`Error parsing or fetching GeoJSON for layer ${layerConfig.id}:`, e);
          }
        }
      }


      console.log('Setting new GeoJSONs and interactive layers:', {
        geojsonKeys: Object.keys(newGeojsons),
        interactiveLayers: newInteractiveLayers,
        visibleLayers
      });
      setGeojsons(newGeojsons);
      setInteractiveLayers(newInteractiveLayers);
      setLoading(false);
    };

    fetchData();
    // Only re-run when the visibleLayers array or the base layers config changes
  }, [visibleLayers, layers]);

  const handleMapClick = (e: MapLayerMouseEvent) => {
    // Disable popups during measurement mode
    if (measureActive) {
      return;
    }

    if (!map) {
      console.log('No map instance available');
      return;
    }

    // Get the actual mapbox map instance
    const mapInstance = map && typeof map.getMap === 'function' ? map.getMap() : map;
    if (!mapInstance) {
      console.log('No mapbox map instance available');
      return;
    }

    console.log('Map click detected at:', e.point);

    // Get all rendered features at click point
    const features = mapInstance.queryRenderedFeatures(e.point);

    console.log('All features found at click point:', features);

    // Filter features to only include our permanent layers
    const permanentLayerFeatures = features.filter(feature => {
      const layerId = feature.layer?.id || '';
      console.log('Checking layer:', layerId, 'against visible layers:', visibleLayers);
      // Check if this is one of our permanent layer IDs
      const isPermanentLayer = visibleLayers.some(visibleLayerId =>
        layerId.startsWith(`${visibleLayerId}-`)
      );
      console.log('Is permanent layer:', isPermanentLayer);
      return isPermanentLayer;
    });

    console.log('Permanent layer features found:', permanentLayerFeatures);

    if (permanentLayerFeatures.length > 0) {
      const feature = permanentLayerFeatures[0];
      const layerId = feature.layer?.id || 'unknown';
      // Extract the base layer ID from the full layer ID (e.g., "couloirs-fill" -> "couloirs")
      const baseLayerId = layerId.split('-')[0];
      const name = feature.properties?.Nom || feature.properties?.Name || feature.properties?.name || 'Unknown';

      console.log('Selected feature:', { layerId, baseLayerId, name, properties: feature.properties });

      // For PPEM features, use the custom header format for display
      const displayName = baseLayerId === 'petite-mine' && feature.properties?.Code
        ? `${feature.properties.Code} D2`
        : name;

      setPopupState({
        feature: {
          name: displayName,
          properties: feature.properties || {},
          layerType: baseLayerId,
        },
        position: e.point,
      });

      // Add highlight when popup opens
      addFeatureHighlight(baseLayerId, displayName);
    } else {
      console.log('No permanent layer features found at click point.');
      removeFeatureHighlight(); // Remove highlight when clicking on empty space
      setPopupState(null); // Close popup if clicking on empty space
    }
  };

  // Setup a single click handler for the map
  useEffect(() => {
    if (!map) return;

    console.log('Attaching single map click handler.');
    map.on('click', handleMapClick);

    // Cleanup
    return () => {
      console.log('Removing map click handler.');
      map.off('click', handleMapClick);
    };
  }, [map, measureActive, visibleLayers]); // Reruns when map, measureActive, or visibleLayers change

  // Render Mapbox GL layers for each visible permanent layer
  // Assumes parent MapboxMap provides <Map> context
  return (
    <>
      {canEdit && map && (
        <LayerActionPanel
          map={map && typeof map.getMap === 'function' ? map.getMap() : map}
          isOpen={layerActionPanelOpen}
          onClose={onLayerActionClose}
        />
      )}
      {Object.entries(geojsons).map(([layerId, geojson]) => {
        // Find the full config for this layer to get style info from permanentLayersGeojson
        // Handle both regular layers and child layers in groups
        let styleConfig = permanentLayersGeojson.find((c) => c.id === layerId);
        if (!styleConfig) {
          // Look for child layers in group structures
          for (const groupLayer of permanentLayersGeojson) {
            if (groupLayer.type === 'group' && groupLayer.children) {
              const childConfig = groupLayer.children.find(child => child.id === layerId);
              if (childConfig) {
                styleConfig = childConfig;
                break;
              }
            }
          }
        }

        // Get layer name from styleConfig or fallback
        const layerName = styleConfig?.name || layerId;

        // Fully skip if config or geojson is invalid
        const isValidGeojson = geojson && typeof geojson === 'object' && geojson.type === 'FeatureCollection' && Array.isArray(geojson.features);
        if (!styleConfig) {
          console.warn(`No config found for layerId: ${layerId} in rendering. styleConfig=${!!styleConfig}`);
          return null;
        }
        if (!isValidGeojson) {
          console.warn(`Skipping layer ${layerId} due to invalid or null geojson.`);
          return null;
        }
        console.log(`Rendering layer: ${layerName}`, { layerId, hasFeatures: geojson.features?.length, styleConfig: !!styleConfig });
        return (
          <React.Fragment key={layerId}>
            {/* Fill layer */}
            <Source id={`${layerId}-src`} type="geojson" data={geojson}>
              {(() => {
                // Check if we have valid features and geometry
                const hasFeatures = geojson.features && geojson.features.length > 0;
                const geomType = hasFeatures ? geojson.features[0]?.geometry?.type : null;
                const isPointLayer = geomType === 'Point';

                console.log(`Layer ${layerId}: hasFeatures=${hasFeatures}, geomType=${geomType}, isPointLayer=${isPointLayer}`, { styleConfig: styleConfig.style });

                return isPointLayer ? (
                  <>
                    <Layer
                      id={`${layerId}-circle`}
                      type="circle"
                      source={`${layerId}-src`}
                      paint={{
                        'circle-radius': (layerId === 'mejhars' || layerId === 'forages') ? 3 : 4,
                        'circle-color': layerId === 'mejhars' ? '#FF0000' : (layerId === 'forages' ? '#87CEEB' : (styleConfig.style?.fillColor || '#E91E63')),
                        'circle-stroke-width': styleConfig.style?.weight || 1,
                        'circle-stroke-color': layerId === 'mejhars' ? '#000000' : (layerId === 'forages' ? '#000000' : (styleConfig.style?.color || '#FFFFFF'))
                      }}
                    />
                    {labelsVisible && (
                      <Layer
                        id={`${layerId}-label`}
                        type="symbol"
                        source={`${layerId}-src`}
                        layout={{
                          'text-field': ['get', 'Nom'],
                          'text-offset': [0, 1.2],
                          'text-size': 12
                        }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <Layer
                      id={`${layerId}-fill`}
                      type="fill"
                      source={`${layerId}-src`}
                      paint={{
                        'fill-color': styleConfig.style?.fillColor || '#1569ef',
                        'fill-opacity': typeof styleConfig.style?.fillOpacity === 'number' ? styleConfig.style.fillOpacity : 0.3
                      }}
                    />
                    <Layer
                      id={`${layerId}-outline`}
                      type="line"
                      source={`${layerId}-src`}
                      paint={{
                        'line-color': styleConfig.style?.color || '#333333',
                        'line-width': typeof styleConfig.style?.weight === 'number' ? styleConfig.style.weight : 1,
                        'line-opacity': (styleConfig.style as any)?.lineOpacity || 1
                      }}
                    />
                  </>
                );
              })()}
            </Source>
          </React.Fragment>
        );
      })}
      {/* Render popup as portal to document body */}
      {popupState && createPortal(
        <FeaturePopup
          feature={popupState.feature}
          position={popupState.position}
          onClose={() => {
            // Remove highlight when popup closes
            removeFeatureHighlight();
            setPopupState(null);
          }}
          onZoom={async () => {
            if (!map || !popupState.feature) return;
            try {
              const m = map && typeof map.getMap === 'function' ? map.getMap() : map;
              const mbx = m as mapboxgl.Map;
              // Only highlight if geometry is available
              const layerId = popupState.feature.layerType;
              const geojson = geojsons[layerId];
              if (!geojson) return;
              // Find the feature by name or code (for PPEM features)
              let match;
              if (layerId === 'petite-mine') {
                // For PPEM features, match by Code
                const codeFromHeader = popupState.feature.name.replace(' D2', ''); // Extract code from header
                match = geojson.features.find(f => f.properties && f.properties.Code === codeFromHeader);
              } else {
                // For other features, match by name
                match = geojson.features.find(f => f.properties && (f.properties.Nom === popupState.feature.name || f.properties.Name === popupState.feature.name));
              }
              if (!match) return;
              // Zoom to bounds
              if (match.geometry.type === 'Polygon' || match.geometry.type === 'MultiPolygon') {
                // Compute bbox
                const coords = match.geometry.type === 'Polygon'
                  ? match.geometry.coordinates.flat(1)
                  : match.geometry.coordinates.flat(2);
                const lons = coords.map(c => c[0]);
                const lats = coords.map(c => c[1]);
                const minLng = Math.min(...lons), maxLng = Math.max(...lons);
                const minLat = Math.min(...lats), maxLat = Math.max(...lats);
                mbx.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, duration: 900 });
              } else if (match.geometry.type === 'Point') {
                const [lng, lat] = match.geometry.coordinates;
                mbx.flyTo({ center: [lng, lat], zoom: 16 });
              }
              // Add highlight layer
              const highlightId = 'feature-highlight';
              // Remove if exists
              if (mbx.getLayer(highlightId)) mbx.removeLayer(highlightId);
              if (mbx.getSource(highlightId)) mbx.removeSource(highlightId);
              mbx.addSource(highlightId, { type: 'geojson', data: match });

              // Different highlight styling based on layer type
              if (layerId === 'petite-mine') {
                // For PPEM features, add a thicker outline
                mbx.addLayer({
                  id: highlightId,
                  type: 'line',
                  source: highlightId,
                  paint: {
                    'line-color': '#ff0000',
                    'line-width': 3,
                    'line-opacity': 0.8
                  }
                });
              } else {
                // For other features, use the default blue highlight
                mbx.addLayer({
                  id: highlightId,
                  type: 'line',
                  source: highlightId,
                  paint: {
                    'line-color': '#1976d2',
                    'line-width': 2,
                    'line-opacity': 0.9
                  }
                });
              }

            } catch (e) {
              // fail silently
            }
          }}
        />,
        document.body
      )}
      {/* Debug interactive layers - remove later */}
      <div style={{ display: 'none' }}>
        Active interactive layers: {interactiveLayers.join(', ')}
      </div>
    </>
  );
};

export default EnhancedPermanentLayers;
